import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notification } from "@/components/admin/notification";
import { AuthVisualPanel } from "./AuthVisualPanel";

const REQUEST_EMAIL = "support@playanoedu.com";

export const RequestAccessPage = () => {
  const translate = useTranslate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const subject = encodeURIComponent("Access Request – Raisy");
    const body = encodeURIComponent(
      `Hi,\n\nI would like to request access to Raisy.\n\nName: ${firstName} ${lastName}\nEmail: ${email}\n\nThank you.`
    );

    window.location.href = `mailto:${REQUEST_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const isValid = firstName.trim() && lastName.trim() && email.trim();

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <AuthVisualPanel />
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                {translate("crm.auth.request_access.title", {
                  _: "Request Access",
                })}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {translate("crm.auth.request_access.subtitle", {
                  _: "Fill in your details and your email client will open so you can send us a request.",
                })}
              </p>
            </div>

            {submitted ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.auth.request_access.submitted", {
                    _: "Your email client should have opened. Send the email and we'll be in touch once your request is reviewed.",
                  })}
                </p>
                <Link
                  to="/login"
                  className="block text-sm text-center hover:underline"
                >
                  {translate("crm.auth.request_access.back_to_login", {
                    _: "Back to sign in",
                  })}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="first_name">
                    {translate("crm.auth.first_name", { _: "First name" })}
                  </Label>
                  <Input
                    id="first_name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="last_name">
                    {translate("crm.auth.last_name", { _: "Last name" })}
                  </Label>
                  <Input
                    id="last_name"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">
                    {translate("ra.auth.email", { _: "Email" })}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={!isValid} className="w-full">
                  {translate("crm.auth.request_access.submit", {
                    _: "Send request",
                  })}
                </Button>
                <Link
                  to="/login"
                  className="block text-sm text-center hover:underline"
                >
                  {translate("crm.auth.request_access.back_to_login", {
                    _: "Back to sign in",
                  })}
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};

RequestAccessPage.path = "/request-access";
